/**
 * DM and group DM calls (2026-11-01), as Discord has them:
 *
 * - `CallButton`: the Call button in a DM's header (Start voice call, or
 *   Join call while one runs there). Hidden while calls (or voice) are off,
 *   and across a block.
 * - `DmCallBand`: the call at the top of the DM, Discord's call view. In the
 *   call: the voice room's stage (the people as tiles, live screens and
 *   cameras, the talk ring) with the members not in it below (Ringing…, or
 *   Ring), and the voice controls on their glass bar (mic, sound, camera, Go
 *   Live, settings, leave). Not in it: who is in it, and Join (or Accept and
 *   Decline while it rings you).
 * - `IncomingCalls`: the ringing card at the window's top (Accept, Decline)
 *   for a call ringing you anywhere in the app.
 *
 * Names are the people's own text: `dir="auto"` in a <bdi>.
 */
import { ChannelType, RelationshipType, type Channel, type Snowflake } from '@neosocial/protocol';
import { useLayout } from '@neosocial/ui/layout';
import { useId, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import {
  callPeople,
  declineCall,
  incomingCalls,
  joinCall,
  ringAgain,
  useCalls,
  type DmCall,
} from '../store/calls';
import { useFlag } from '../store/config';
import { useData, useDataShallow } from '../store/data';
import { displayName, privateChannelName } from '../store/selectors';
import { openSettings } from '../store/ui';
import { Avatar, Btn, Icon, IconBtn } from '../ui/kit';
import { portalRoot } from '../ui/overlay';
import { leaveVoice, toggleDeaf, toggleMute } from '../voice/controller';
import { micToggleView, soundToggleView } from '../voice/flags';
import { rosterPeople } from '../voice/machine';
import { useVoice } from '../voice/store';
import { SelfToggleButton, useOwnServerFlags } from '../voice/VoiceFlags';
import { CameraButton, ShareButton } from './Streams';
import { HoldToTalk, openInputMenu, PttChip, Stage } from './VoiceRoom';
import { openOutputMenu } from './voiceQuick';

/** One empty list for selectors (a new [] each read would re-render forever). */
const NOBODY: Snowflake[] = [];

/** Calls are on for you: voice and the Console's `dm_calls`. */
function useCallsOn(): boolean {
  const voice = useFlag('voice');
  const calls = useFlag('dm_calls');
  return voice && calls;
}

/** Your voice session in this DM's call: joining (`connecting`), in it, or not. */
function useHere(channelId: Snowflake): 'connected' | 'connecting' | null {
  return useVoice((v) => {
    const s = v.session;
    if (s.status === 'idle' || (s.channelId ?? s.targetId) !== channelId) return null;
    if (s.status === 'failed') return null;
    return s.status === 'connected' ? 'connected' : 'connecting';
  });
}

/** The people in the call: the live roster while you are in it, else the call's voice states. */
function useCallPeople(channelId: Snowflake, here: boolean): Snowflake[] {
  const meId = useData((d) => d.me?.id);
  const fromRoster = useVoice((v) => {
    if (!here || v.session.pid === null) return '';
    const ids = rosterPeople(v.session, meId, channelId).map((m) => m.user_id);
    if (meId && !ids.includes(meId)) ids.push(meId);
    return ids.join(',');
  });
  const fromCall = useCalls((s) => callPeople(s.calls[channelId]).join(','));
  const list = here && fromRoster ? fromRoster : fromCall;
  return list ? list.split(',') : [];
}

/* -------------------------------- the button -------------------------------- */

/** The DM header's Call button: start a call, or join the one running here. */
export function CallButton({ channel }: { channel: Channel }) {
  const { t } = useT();
  const on = useCallsOn();
  const here = useHere(channel.id);
  const running = useCalls((s) => callPeople(s.calls[channel.id]).length > 0);
  // a 1:1 DM across a block is closed to calls, as to messages
  const blocked = useData((d) => {
    if (channel.type !== ChannelType.DM) return false;
    const peer = channel.recipients?.find((u) => u.id !== d.me?.id);
    return !!peer && d.relationships[peer.id]?.type === RelationshipType.BLOCKED;
  });
  if (!on || blocked || here) return null;
  const label = t(running ? 'call.join' : 'call.start');
  return (
    <IconBtn
      icon="phone"
      className="cl-call-btn"
      label={label}
      tipPos="bottom"
      data-running={running || undefined}
      onClick={() => void joinCall(channel.id)}
    />
  );
}

/* --------------------------------- the band --------------------------------- */

/** The call at the top of a DM: shown while one runs there, or while you are joining it. */
export function DmCallBand({ channelId }: { channelId: Snowflake }) {
  const on = useCallsOn();
  const here = useHere(channelId);
  const call = useCalls((s) => s.calls[channelId]);
  const people = useCallPeople(channelId, !!here);
  if (!on || (!here && (!call || !people.length))) return null;
  return here ? (
    <InCall channelId={channelId} state={here} people={people} />
  ) : (
    <CallOutside channelId={channelId} call={call!} people={people} />
  );
}

/** You are in the call: the stage, who is not in it, and the controls. */
function InCall({
  channelId,
  state,
  people,
}: {
  channelId: Snowflake;
  state: 'connected' | 'connecting';
  people: Snowflake[];
}) {
  const { t } = useT();
  const s = useVoice((v) => v.session);
  const ptt = useVoice((v) => v.prefs.inputMode === 'ptt');
  const meId = useData((d) => d.me?.id);
  const lay = useLayout();
  const touch = lay.pointer === 'coarse';
  const own = useOwnServerFlags();
  const connected = state === 'connected';
  const noMic = s.listenOnly && s.pid !== null;
  // a DM call has no moderators: only your own mute and deafen (and no microphone)
  const mic = micToggleView(
    {
      selfMute: s.selfMute,
      selfDeaf: s.selfDeaf,
      serverMute: false,
      serverDeaf: false,
      channelMute: false,
      channelDeaf: false,
      noTalk: connected && own.noTalk,
      lift: { mute: false, deaf: false },
    },
    { name: 'hubv.vr.mute', undo: 'hubv.vr.unmute' },
  );
  const sound = soundToggleView(
    { selfDeaf: s.selfDeaf, serverDeaf: false, channelDeaf: false, lift: false },
    { name: 'dock.deafen', undo: 'dock.undeafen' },
  );
  const tiles = people.map((userId) => ({ userId }));
  return (
    <section className="cl-band" data-state={state} aria-label={t('call.band')}>
      <div className="hubv-vr cl-vr" data-state={connected ? 'connected' : 'connecting'}>
        <div className="hubv-vr-main">
          <Stage
            people={tiles}
            s={s}
            meId={meId}
            guildId={null}
            channelId={channelId}
            chatOver={false}
          />
          {!connected ? (
            <div className="hubv-vr-conn" aria-hidden="true">
              <span>
                <i className="nx-spin" />
                {t(s.reconnecting ? 'ns.voice.reconnecting' : 'call.connecting')}
              </span>
            </div>
          ) : null}
          <div className="hubv-barwrap" data-kind="voice">
            <RingRow channelId={channelId} people={people} />
            {ptt && touch && !noMic ? <HoldToTalk /> : <PttChip />}
            <div
              className="hubv-bar nx-glass strong"
              role="toolbar"
              aria-label={t('call.controls')}
            >
              <div className="hubv-split">
                {noMic ? (
                  <button
                    type="button"
                    className="hubv-cb off"
                    aria-pressed={true}
                    aria-label={t('ns.voice.noMicTip')}
                    data-tip={t('ns.voice.noMicTip')}
                    onClick={() => openSettings('voice')}
                  >
                    <Icon name="mic-off" />
                  </button>
                ) : (
                  <SelfToggleButton
                    view={mic}
                    cls="hubv-cb"
                    onClick={toggleMute}
                    onMenu={(el) => openInputMenu(el.parentElement ?? el)}
                  />
                )}
                {ptt && !noMic ? (
                  <span className="hubv-ptt-tag" aria-hidden="true">
                    {t('hubv.vr.pttTag')}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="hubv-cbx"
                  aria-haspopup="menu"
                  aria-label={t('hubv.vr.inputOptions')}
                  data-tip={t('hubv.vr.inputOptions')}
                  onClick={(e) => openInputMenu(e.currentTarget.parentElement ?? e.currentTarget)}
                >
                  <Icon name="chev-up" size="s" />
                </button>
              </div>
              <SelfToggleButton
                view={sound}
                cls="hubv-cb"
                onClick={toggleDeaf}
                onMenu={openOutputMenu}
              />
              {connected ? (
                <>
                  <CameraButton cls="hubv-cb hubv-cb-cam" channelId={s.channelId} />
                  <ShareButton cls="hubv-cb hubv-cb-share" channelId={s.channelId} labeled />
                </>
              ) : null}
              <button
                type="button"
                className="hubv-cb hubv-cb-set"
                aria-label={t('ns.set.voice')}
                data-tip={t('ns.set.voice')}
                onClick={() => openSettings('voice')}
              >
                <Icon name="gear" />
              </button>
              <span className="hubv-bar-sep" aria-hidden="true" />
              <button
                type="button"
                className="hubv-cb leave"
                aria-label={t('call.leave')}
                data-tip={t('call.leave')}
                onClick={() => leaveVoice()}
              >
                <Icon name="phone-off" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** The members not in the call: being rung (Ringing…), or not (Ring), as Discord lists them. */
function RingRow({ channelId, people }: { channelId: Snowflake; people: Snowflake[] }) {
  const { t } = useT();
  const meId = useData((d) => d.me?.id);
  const others = useDataShallow((d) =>
    (d.channels[channelId]?.recipients ?? []).filter(
      (u) => u.id !== meId && !people.includes(u.id),
    ),
  );
  const ringing = useCalls((s) => s.calls[channelId]?.ringing ?? NOBODY);
  const connected = useVoice((v) => v.session.status === 'connected');
  if (!others.length) return null;
  return (
    <ul className="cl-ring nx-glass" aria-label={t('call.notJoined')}>
      {others.slice(0, 6).map((u) => (
        <RingPerson
          key={u.id}
          userId={u.id}
          rung={ringing.includes(u.id)}
          canRing={connected}
          onRing={() => void ringAgain(channelId, [u.id])}
        />
      ))}
      {others.length > 6 ? <li className="cl-ring-more nx-num">+{others.length - 6}</li> : null}
    </ul>
  );
}

function RingPerson({
  userId,
  rung,
  canRing,
  onRing,
}: {
  userId: Snowflake;
  rung: boolean;
  canRing: boolean;
  onRing: () => void;
}) {
  const { t } = useT();
  const user = useData((d) => d.users[userId]);
  const name = displayName(user);
  return (
    <li className="cl-rp" data-rung={rung || undefined} data-user={userId}>
      <span className="cl-rp-av">
        <Avatar user={user} name={name} size={24} />
      </span>
      <bdi className="cl-rp-name" dir="auto">
        {name}
      </bdi>
      {rung ? (
        <span className="cl-rp-st" role="status">
          {t('call.ringing')}
        </span>
      ) : canRing ? (
        <button
          type="button"
          className="cl-rp-ring"
          aria-label={t('call.ringTip', { name })}
          data-tip={t('call.ringTip', { name })}
          onClick={onRing}
        >
          <Icon name="phone" size="s" />
          <span>{t('call.ring')}</span>
        </button>
      ) : null}
    </li>
  );
}

/** A call runs here and you are not in it: who is in it, and Join (Accept / Decline while it rings you). */
function CallOutside({
  channelId,
  call,
  people,
}: {
  channelId: Snowflake;
  call: DmCall;
  people: Snowflake[];
}) {
  const { t } = useT();
  const meId = useData((d) => d.me?.id);
  const rung = !!meId && call.ringing.includes(meId);
  return (
    <section className="cl-band cl-out" data-state="idle" aria-label={t('call.band')}>
      <div className="cl-out-in nx-arrive">
        <div className="cl-stack" aria-hidden="true">
          {people.slice(0, 5).map((u, i) => (
            <span key={u} className="cl-stk" style={{ '--i': i } as CSSProperties}>
              <StackAvatar userId={u} />
            </span>
          ))}
        </div>
        <p className="cl-out-t">
          <Icon name="phone" size="s" />
          <span>{t('call.count', { n: people.length })}</span>
        </p>
        <div className="cl-out-acts">
          {rung ? (
            <>
              <Btn
                size="sm"
                kind="danger-quiet"
                icon="phone-off"
                onClick={() => void declineCall(channelId)}
              >
                {t('call.in.decline')}
              </Btn>
              <Btn size="sm" kind="primary" icon="phone" onClick={() => void joinCall(channelId)}>
                {t('call.in.accept')}
              </Btn>
            </>
          ) : (
            <Btn size="sm" kind="primary" icon="phone" onClick={() => void joinCall(channelId)}>
              {t('call.join')}
            </Btn>
          )}
        </div>
      </div>
    </section>
  );
}

function StackAvatar({ userId }: { userId: Snowflake }) {
  const user = useData((d) => d.users[userId]);
  return <Avatar user={user} size={40} />;
}

/* ------------------------------ incoming calls ------------------------------ */

/** The ringing cards: one per call ringing you, at the window's top. */
export function IncomingCalls() {
  const { t } = useT();
  const on = useCallsOn();
  const calls = useCalls((s) => s.calls);
  const meId = useData((d) => d.me?.id);
  const here = useVoice((v) =>
    v.session.status === 'idle' ? null : (v.session.channelId ?? v.session.targetId),
  );
  const list = on ? incomingCalls(calls, meId, here) : [];
  if (!list.length) return null;
  return createPortal(
    <section className="cl-in-stack" aria-label={t('call.in.stack')}>
      <ol className="cl-in-list">
        {list.map((c) => (
          <IncomingCard key={c.channelId} call={c} />
        ))}
      </ol>
    </section>,
    portalRoot(),
  );
}

function IncomingCard({ call }: { call: DmCall }) {
  const { t } = useT();
  const id = useId();
  const channel = useData((d) => d.channels[call.channelId]);
  const name = useData((d) => (channel ? privateChannelName(d, channel) : ''));
  // who calls: the first one in the call (the caller), else the DM's other person
  const callerId = callPeople(call)[0] ?? channel?.recipients?.[0]?.id;
  const caller = useData((d) => (callerId ? d.users[callerId] : undefined));
  if (!channel) return null;
  const group = channel.type === ChannelType.GROUP_DM;
  return (
    <li
      className="cl-in nx-glass"
      role="alertdialog"
      aria-labelledby={id + 'n'}
      aria-describedby={id + 's'}
      data-call={call.channelId}
    >
      <span className="cl-in-av">
        <Avatar user={caller} size={48} />
      </span>
      <div className="cl-in-t">
        <bdi className="cl-in-name" dir="auto" id={id + 'n'}>
          {name}
        </bdi>
        <span className="cl-in-sub" id={id + 's'}>
          {t(group ? 'call.in.group' : 'call.in.dm')}
        </span>
      </div>
      <div className="cl-in-acts">
        <button
          type="button"
          className="cl-rb decline"
          aria-label={t('call.in.decline')}
          data-tip={t('call.in.decline')}
          onClick={() => void declineCall(call.channelId)}
        >
          <Icon name="phone-off" />
        </button>
        <button
          type="button"
          className="cl-rb accept"
          aria-label={t('call.in.accept')}
          data-tip={t('call.in.accept')}
          onClick={() => void joinCall(call.channelId)}
        >
          <Icon name="phone" />
        </button>
      </div>
    </li>
  );
}
